const { getCookie, setCookie } = window.BAO.Utils

class PushCartNewsletter extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['form', 'email', 'error', 'success']
  }

  constructor () {
    super()

    this.defaultProperties = {
      signup_source: 'website_cart',
    }
  }

  connectedCallback () {
    super.connectedCallback()

    this.checkVisibility()
  }

  async checkVisibility () {
    if (this.isSubscribed === 'true') {
      // User is already subscribed
      this.ariaHidden = true
    } else if (window.theme.customer.isLoggedIn === true) {
      // Check if profile exists already before submitting.
      const profileExists = await this.checkProfileExists(
        window.theme.customer.email,
      )

      if (profileExists) {
        this.ariaHidden = true

        setCookie(this.subscribedCustomerCookieName, 'true', 7)
      }
    }
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els.form.element,
      'submit',
      this.onFormSubmit.bind(this),
    )

    this.listeners.add(
      this.els.email.element,
      'input',
      this.handleEmailInput.bind(this),
    )
  }

  handleEmailInput () {
    if (this.validEmail()) {
      this.els.email.element.setAttribute('aria-invalid', false)
    } else {
      this.els.email.element.setAttribute('aria-invalid', true)
    }
  }

  async onFormSubmit (e) {
    e.preventDefault()

    if (!window.theme?.ometria) return

    // Only show the outcome of the latest submission.
    this.toggleErrorMessage()
    this.els.success.element.setAttribute('aria-hidden', true)

    const emailAddress = this?.els?.email?.element?.value.replace(/\s/g, '')

    if (!this.validEmail()) {
      this.els.email.element.setAttribute('aria-invalid', true)
      this.els.email.element.focus()
      return
    }

    // Profile lookup disabled until there's an Ometria equivalent of the
    // self-portrait-klaviyo.vercel.app `identify` endpoint.
    /*
    // Check if profile exists already before submitting.
    const profileExists = await this.checkProfileExists(emailAddress)

    // If profile already exists, skip to register form.
    if (profileExists) {
      this.log('Profile already exists')
      this.toggleErrorMessage(window.theme.strings.newsletterEmailInUse)
      return
    }
    */

    this.createClientSubscription({
      emailAddress,
    })
  }

  // Klaviyo API Methods.
  async checkProfileExists (emailAddress) {
    const scriptUrl = 'https://self-portrait-klaviyo.vercel.app/api/identify'

    const data = {
      email: emailAddress,
      store: window.theme?.klaviyo?.store,
    }

    const response = await fetch(scriptUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    const jsonResponse = await response.json()

    return jsonResponse.registered ? jsonResponse.registered : false
  }

  async createClientSubscription (formData) {
    const { emailAddress } = formData
    const properties = { ...this.defaultProperties }

    // Ometria's AJAX forms endpoint takes urlencoded fields, with custom fields
    // prefixed with `properties.`. `email` and `__email` are honeypots and must be empty.
    // https://support.ometria.com/hc/en-gb/articles/360011168777-Setting-up-Ajax-forms
    const bodyData = new URLSearchParams({
      __form_id: window.theme.ometria.formId,
      email: '',
      __email: '',
      '@account': window.theme.ometria.account,
      '@subscription_status': 'SUBSCRIBED',
      ue: emailAddress,
    })

    Object.entries(properties).forEach(([key, value]) => {
      bodyData.append(`properties.${key}`, value)
    })

    const options = {
      method: 'POST',
      headers: {
        'content-type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
      body: bodyData,
    }

    try {
      const response = await fetch(window.theme.ometria.endpoint, options)

      // Returns `{ ok, errors }`, where each error is a `[field, message]` pair.
      const jsonResponse = await response.json()

      if (jsonResponse.ok) {
        this.toggleErrorMessage()
        this.showSuccess()
      } else {
        if (jsonResponse.errors.length > 0) {
          // eslint-disable-next-line no-console
          const errorMessage = jsonResponse.errors[0]?.[1]
            ? jsonResponse.errors[0][1]
            : window.theme.strings.newsletterGenericError
          this.toggleErrorMessage(errorMessage)
        }
      }
    } catch (error) {
      const errorMessage =
        error && error.detail
          ? error.detail
          : window.theme.strings.newsletterGenericError
      this.toggleErrorMessage(errorMessage)
    }
  }

  toggleErrorMessage (message = false) {
    const errorElement = this.els.error.element
    if (!message) {
      errorElement.setAttribute('aria-hidden', true)
    } else {
      errorElement.setAttribute('aria-hidden', false)
      errorElement.innerHTML = message
    }
  }

  async showSuccess () {
    const trigger = this.els?.['trigger-text']?.element
    const success = this.els?.success?.element
    const submit = this.els?.submit?.element

    if (submit) {
      submit.remove()
    }

    if (success) {
      success.removeAttribute('aria-hidden')
    }

    if (trigger && success) {
      trigger.innerText = success.innerText
    }

    await window.BAO.Cart.updateCartDiscount(this.dataset.discountCode)

    setCookie(this.subscribedCustomerCookieName, 'true', 7)

    trigger.click()

    setTimeout(() => {
      this.ariaHidden = true
    }, 3000)
  }

  validEmail () {
    const emailAddress = this?.els?.email?.element?.value.replace(/\s/g, '')

    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
    return emailRegex.test(emailAddress)
  }

  get isSubscribed () {
    return getCookie(this.subscribedCustomerCookieName)
  }

  get subscribedCustomerCookieName () {
    return `BAO-subscribedCustomer`
  }
}

if (!customElements.get('push-cart-newsletter')) {
  customElements.define('push-cart-newsletter', PushCartNewsletter)
}
